import React from "react"
import { ChampType, pointsStructureType, userChampSnapshotType, RoundType, fullUserType, CompetitorEntryType } from "../../shared/types"
import { getCompetitorsFromRound, isCompetitorInactive } from "../../shared/utility"
import { ChampSettingsFormType } from "./Views/ChampSettings/ChampSettings"
import { AutomationFormType } from "./Views/Automation/Automation"
import { AdminFormType } from "./Views/Admin/Admin"
import { ProtestsFormType, RuleChangesFormType } from "../../shared/formValidation"
import { StatItem } from "./components/ChampBannerStats/ChampBannerStats"
import SportsMotorsportsIcon from "@mui/icons-material/SportsMotorsports"
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium"
import PersonIcon from "@mui/icons-material/Person"

// ============================================
// Form Initialization Functions
// ============================================

// Initialize settings form state from championship data.
export const initSettingsForm = (champ: ChampType): ChampSettingsFormType => ({
  champName: champ.name,
  rounds: champ.rounds.length,
  maxCompetitors: champ.settings.maxCompetitors,
  pointsStructure: champ.pointsStructure,
  icon: null,
  profile_picture: null,
  skipCountDown: champ.settings.skipCountDown ?? false,
  skipResults: champ.settings.skipResults ?? false,
  inviteOnly: champ.settings.inviteOnly,
  active: champ.active,
  series: champ.series,
  competitorsCanBet: champ.settings.competitorsCanBet ?? true,
})

// Initialize automation form state from championship data.
export const initAutomationForm = (champ: ChampType): AutomationFormType => ({
  enabled: champ.settings.automation?.enabled ?? false,
  autoOpen: champ.settings.automation?.bettingWindow?.autoOpen ?? false,
  autoOpenTime: champ.settings.automation?.bettingWindow?.autoOpenTime ?? 10,
  autoClose: champ.settings.automation?.bettingWindow?.autoClose ?? false,
  autoCloseTime: champ.settings.automation?.bettingWindow?.autoCloseTime ?? 5,
  autoNextRound: champ.settings.automation?.round?.autoNextRound ?? false,
  autoNextRoundTime: champ.settings.automation?.round?.autoNextRoundTime ?? 60,
})

// Initialize protests form state from championship data.
export const initProtestsForm = (champ: ChampType): ProtestsFormType => {
  const expiryDays = Math.round((champ.settings.protests?.expiry ?? 10080) / 1440)
  return {
    alwaysVote: champ.settings.protests?.alwaysVote ?? false,
    allowMultiple: champ.settings.protests?.allowMultiple ?? false,
    expiry: expiryDays,
  }
}

// Initialize rule changes form state from championship data.
export const initRuleChangesForm = (champ: ChampType): RuleChangesFormType => {
  const expiryDays = Math.round((champ.settings.ruleChanges?.expiry ?? 10080) / 1440)
  return {
    alwaysVote: champ.settings.ruleChanges?.alwaysVote ?? false,
    allowMultiple: champ.settings.ruleChanges?.allowMultiple ?? false,
    expiry: expiryDays,
  }
}

// Initialize admin form state from championship data.
export const initAdminForm = (champ: ChampType): AdminFormType => ({
  adjCanSeeBadges: champ.settings.admin?.adjCanSeeBadges ?? true,
})

// ============================================
// Change Detection Functions
// ============================================

// Check if settings form has changes compared to championship data.
export const hasSettingsChanged = (form: ChampSettingsFormType, champ: ChampType): boolean => {
  return (
    form.champName !== champ.name ||
    form.rounds !== champ.rounds.length ||
    form.maxCompetitors !== champ.settings.maxCompetitors ||
    JSON.stringify(form.pointsStructure) !== JSON.stringify(champ.pointsStructure) ||
    form.icon !== null ||
    form.profile_picture !== null ||
    form.skipCountDown !== (champ.settings.skipCountDown ?? false) ||
    form.skipResults !== (champ.settings.skipResults ?? false) ||
    form.inviteOnly !== champ.settings.inviteOnly ||
    form.active !== champ.active ||
    form.series?._id !== champ.series._id ||
    form.competitorsCanBet !== (champ.settings.competitorsCanBet ?? true)
  )
}

// Check if automation form has changes compared to championship data.
export const hasAutomationChanged = (form: AutomationFormType, champ: ChampType): boolean => {
  return (
    form.enabled !== (champ.settings.automation?.enabled ?? false) ||
    form.autoOpen !== (champ.settings.automation?.bettingWindow?.autoOpen ?? false) ||
    form.autoOpenTime !== (champ.settings.automation?.bettingWindow?.autoOpenTime ?? 10) ||
    form.autoClose !== (champ.settings.automation?.bettingWindow?.autoClose ?? false) ||
    form.autoCloseTime !== (champ.settings.automation?.bettingWindow?.autoCloseTime ?? 5) ||
    form.autoNextRound !== (champ.settings.automation?.round?.autoNextRound ?? false) ||
    form.autoNextRoundTime !== (champ.settings.automation?.round?.autoNextRoundTime ?? 60)
  )
}

// Check if protests form has changes compared to championship data.
export const hasProtestsChanged = (form: ProtestsFormType, champ: ChampType): boolean => {
  return (
    form.alwaysVote !== (champ.settings.protests?.alwaysVote ?? false) ||
    form.allowMultiple !== (champ.settings.protests?.allowMultiple ?? false) ||
    form.expiry !== Math.round((champ.settings.protests?.expiry ?? 10080) / 1440)
  )
}

// Check if rule changes form has changes compared to championship data.
export const hasRuleChangesChanged = (form: RuleChangesFormType, champ: ChampType): boolean => {
  return (
    form.alwaysVote !== (champ.settings.ruleChanges?.alwaysVote ?? false) ||
    form.allowMultiple !== (champ.settings.ruleChanges?.allowMultiple ?? false) ||
    form.expiry !== Math.round((champ.settings.ruleChanges?.expiry ?? 10080) / 1440)
  )
}

// Check if admin form has changes compared to championship data.
export const hasAdminChanged = (form: AdminFormType, champ: ChampType): boolean => {
  return form.adjCanSeeBadges !== (champ.settings.admin?.adjCanSeeBadges ?? true)
}

// ============================================
// Updates Builder Types
// ============================================

export interface SettingsUpdatesType {
  name?: string
  rounds?: number
  maxCompetitors?: number
  pointsStructure?: pointsStructureType
  icon?: string
  skipCountDown?: boolean
  skipResults?: boolean
  inviteOnly?: boolean
  active?: boolean
  series?: string
  profile_picture?: string
  competitorsCanBet?: boolean
}

export interface AutomationUpdatesType {
  enabled?: boolean
  bettingWindow?: {
    autoOpen?: boolean
    autoOpenTime?: number
    autoClose?: boolean
    autoCloseTime?: number
  }
  round?: {
    autoNextRound?: boolean
    autoNextRoundTime?: number
  }
}

export interface ProtestsUpdatesType {
  alwaysVote?: boolean
  allowMultiple?: boolean
  expiry?: number
}

export interface RuleChangesUpdatesType {
  alwaysVote?: boolean
  allowMultiple?: boolean
  expiry?: number
}

// ============================================
// Updates Builder Functions
// ============================================

// Build settings updates object with only changed fields (excluding file uploads).
export const buildSettingsUpdates = (
  form: ChampSettingsFormType,
  champ: ChampType,
): SettingsUpdatesType => {
  const updates: SettingsUpdatesType = {}

  if (form.champName !== champ.name) {
    updates.name = form.champName
  }

  if (form.rounds !== champ.rounds.length) {
    updates.rounds = form.rounds
  }

  if (form.maxCompetitors !== champ.settings.maxCompetitors) {
    updates.maxCompetitors = form.maxCompetitors
  }

  if (JSON.stringify(form.pointsStructure) !== JSON.stringify(champ.pointsStructure)) {
    updates.pointsStructure = form.pointsStructure
  }

  if (form.skipCountDown !== (champ.settings.skipCountDown ?? false)) {
    updates.skipCountDown = form.skipCountDown
  }

  if (form.skipResults !== (champ.settings.skipResults ?? false)) {
    updates.skipResults = form.skipResults
  }

  if (form.inviteOnly !== champ.settings.inviteOnly) {
    updates.inviteOnly = form.inviteOnly
  }

  if (form.active !== champ.active) {
    updates.active = form.active
  }

  if (form.series?._id !== champ.series._id && form.series?._id) {
    updates.series = form.series._id
  }

  if (form.competitorsCanBet !== (champ.settings.competitorsCanBet ?? true)) {
    updates.competitorsCanBet = form.competitorsCanBet
  }

  return updates
}

// Build automation updates object with only changed fields.
export const buildAutomationUpdates = (
  form: AutomationFormType,
  champ: ChampType,
): AutomationUpdatesType => {
  const updates: AutomationUpdatesType = {}

  if (form.enabled !== (champ.settings.automation?.enabled ?? false)) {
    updates.enabled = form.enabled
  }

  const bettingWindowUpdates: AutomationUpdatesType["bettingWindow"] = {}

  if (form.autoOpen !== (champ.settings.automation?.bettingWindow?.autoOpen ?? false)) {
    bettingWindowUpdates.autoOpen = form.autoOpen
  }
  if (form.autoOpenTime !== (champ.settings.automation?.bettingWindow?.autoOpenTime ?? 10)) {
    bettingWindowUpdates.autoOpenTime = form.autoOpenTime
  }
  if (form.autoClose !== (champ.settings.automation?.bettingWindow?.autoClose ?? false)) {
    bettingWindowUpdates.autoClose = form.autoClose
  }
  if (form.autoCloseTime !== (champ.settings.automation?.bettingWindow?.autoCloseTime ?? 5)) {
    bettingWindowUpdates.autoCloseTime = form.autoCloseTime
  }

  if (Object.keys(bettingWindowUpdates).length > 0) {
    updates.bettingWindow = bettingWindowUpdates
  }

  const roundUpdates: AutomationUpdatesType["round"] = {}

  if (form.autoNextRound !== (champ.settings.automation?.round?.autoNextRound ?? false)) {
    roundUpdates.autoNextRound = form.autoNextRound
  }
  if (form.autoNextRoundTime !== (champ.settings.automation?.round?.autoNextRoundTime ?? 60)) {
    roundUpdates.autoNextRoundTime = form.autoNextRoundTime
  }

  if (Object.keys(roundUpdates).length > 0) {
    updates.round = roundUpdates
  }

  return updates
}

// Build protests updates object with only changed fields.
export const buildProtestsUpdates = (
  form: ProtestsFormType,
  champ: ChampType,
): ProtestsUpdatesType => {
  const updates: ProtestsUpdatesType = {}

  if (form.alwaysVote !== (champ.settings.protests?.alwaysVote ?? false)) {
    updates.alwaysVote = form.alwaysVote
  }

  if (form.allowMultiple !== (champ.settings.protests?.allowMultiple ?? false)) {
    updates.allowMultiple = form.allowMultiple
  }

  const currentExpiryDays = Math.round((champ.settings.protests?.expiry ?? 10080) / 1440)
  if (form.expiry !== currentExpiryDays) {
    updates.expiry = form.expiry * 1440
  }

  return updates
}

// Build rule changes updates object with only changed fields.
export const buildRuleChangesUpdates = (
  form: RuleChangesFormType,
  champ: ChampType,
): RuleChangesUpdatesType => {
  const updates: RuleChangesUpdatesType = {}

  if (form.alwaysVote !== (champ.settings.ruleChanges?.alwaysVote ?? false)) {
    updates.alwaysVote = form.alwaysVote
  }

  if (form.allowMultiple !== (champ.settings.ruleChanges?.allowMultiple ?? false)) {
    updates.allowMultiple = form.allowMultiple
  }

  const currentExpiryDays = Math.round((champ.settings.ruleChanges?.expiry ?? 10080) / 1440)
  if (form.expiry !== currentExpiryDays) {
    updates.expiry = form.expiry * 1440
  }

  return updates
}

// ============================================
// Optimistic Update Functions
// ============================================

// Apply settings updates optimistically to championship state.
export const applySettingsOptimistically = (
  champ: ChampType,
  updates: SettingsUpdatesType,
  form: ChampSettingsFormType,
): ChampType => {
  const optimisticChamp = { ...champ }

  if (updates.name) {
    optimisticChamp.name = updates.name
  }

  if (updates.pointsStructure) {
    optimisticChamp.pointsStructure = updates.pointsStructure
  }

  if (updates.maxCompetitors) {
    optimisticChamp.settings = {
      ...optimisticChamp.settings,
      maxCompetitors: updates.maxCompetitors,
    }
  }

  if (typeof updates.skipCountDown === "boolean") {
    optimisticChamp.settings = {
      ...optimisticChamp.settings,
      skipCountDown: updates.skipCountDown,
    }
  }

  if (typeof updates.skipResults === "boolean") {
    optimisticChamp.settings = {
      ...optimisticChamp.settings,
      skipResults: updates.skipResults,
    }
  }

  if (typeof updates.inviteOnly === "boolean") {
    optimisticChamp.settings = {
      ...optimisticChamp.settings,
      inviteOnly: updates.inviteOnly,
    }
  }

  if (typeof updates.active === "boolean") {
    optimisticChamp.active = updates.active
  }

  if (updates.icon) {
    optimisticChamp.icon = updates.icon
  }

  if (updates.profile_picture) {
    optimisticChamp.profile_picture = updates.profile_picture
  }

  if (updates.rounds) {
    const currentRoundsCount = champ.rounds.length

    if (updates.rounds > currentRoundsCount) {
      const newRounds = [...champ.rounds]
      for (let i = currentRoundsCount + 1; i <= updates.rounds; i++) {
        newRounds.push({
          round: i,
          status: "waiting" as const,
          statusChangedAt: null,
          bettingCloseAt: null,
          resultsProcessed: false,
          winner: null,
          runnerUp: null,
          competitors: [],
          drivers: [],
          randomisedDrivers: [],
          teams: [],
        })
      }
      optimisticChamp.rounds = newRounds
    } else if (updates.rounds < currentRoundsCount) {
      optimisticChamp.rounds = champ.rounds.slice(0, updates.rounds)
    }
  }

  if (updates.series && form.series) {
    optimisticChamp.series = form.series
  }

  if (typeof updates.competitorsCanBet === "boolean") {
    optimisticChamp.settings = {
      ...optimisticChamp.settings,
      competitorsCanBet: updates.competitorsCanBet,
    }
  }

  return optimisticChamp
}

// Apply automation updates optimistically to championship state.
export const applyAutomationOptimistically = (
  champ: ChampType,
  updates: AutomationUpdatesType,
): ChampType => {
  return {
    ...champ,
    settings: {
      ...champ.settings,
      automation: {
        ...champ.settings.automation,
        enabled: updates.enabled ?? champ.settings.automation?.enabled ?? false,
        bettingWindow: {
          ...champ.settings.automation.bettingWindow,
          autoOpen:
            updates.bettingWindow?.autoOpen ?? champ.settings.automation.bettingWindow.autoOpen,
          autoOpenTime:
            updates.bettingWindow?.autoOpenTime ??
            champ.settings.automation.bettingWindow.autoOpenTime,
          autoClose:
            updates.bettingWindow?.autoClose ?? champ.settings.automation.bettingWindow.autoClose,
          autoCloseTime:
            updates.bettingWindow?.autoCloseTime ??
            champ.settings.automation.bettingWindow.autoCloseTime,
        },
        round: {
          ...champ.settings.automation.round,
          autoNextRound:
            updates.round?.autoNextRound ?? champ.settings.automation.round.autoNextRound,
          autoNextRoundTime:
            updates.round?.autoNextRoundTime ?? champ.settings.automation.round.autoNextRoundTime,
        },
      },
    },
  }
}

// Apply protests updates optimistically to championship state.
export const applyProtestsOptimistically = (
  champ: ChampType,
  updates: ProtestsUpdatesType,
): ChampType => {
  return {
    ...champ,
    settings: {
      ...champ.settings,
      protests: {
        ...champ.settings.protests,
        alwaysVote: updates.alwaysVote ?? champ.settings.protests.alwaysVote,
        allowMultiple: updates.allowMultiple ?? champ.settings.protests.allowMultiple,
        expiry: updates.expiry ?? champ.settings.protests.expiry,
      },
    },
  }
}

// Apply rule changes updates optimistically to championship state.
export const applyRuleChangesOptimistically = (
  champ: ChampType,
  updates: RuleChangesUpdatesType,
): ChampType => {
  return {
    ...champ,
    settings: {
      ...champ.settings,
      ruleChanges: {
        ...champ.settings.ruleChanges,
        alwaysVote: updates.alwaysVote ?? champ.settings.ruleChanges.alwaysVote,
        allowMultiple: updates.allowMultiple ?? champ.settings.ruleChanges.allowMultiple,
        expiry: updates.expiry ?? champ.settings.ruleChanges.expiry,
      },
    },
  }
}

// Admin updates type.
export interface AdminUpdatesType {
  adjCanSeeBadges?: boolean
}

// Apply admin updates optimistically to championship state.
export const applyAdminOptimistically = (
  champ: ChampType,
  updates: AdminUpdatesType,
): ChampType => {
  return {
    ...champ,
    settings: {
      ...champ.settings,
      admin: {
        ...champ.settings.admin,
        adjCanSeeBadges: updates.adjCanSeeBadges ?? champ.settings.admin?.adjCanSeeBadges ?? true,
      },
    },
  }
}

// ============================================
// Stats Builder Functions
// ============================================

// Configuration options for buildChampBannerStats.
interface ChampBannerStatsOptions {
  showDrivers?: boolean
}

// Builds stats array for ChampBannerStats component display.
export const buildChampBannerStats = (
  champ: ChampType,
  viewedRoundNumber?: number,
  options?: ChampBannerStatsOptions
): StatItem[] => {
  const { showDrivers = true } = options ?? {}
  const displayedRound = viewedRoundNumber ?? 0
  // Get badge counts from championship data.
  const totalBadges = champ.champBadges?.length || 0
  const discoveredBadges = champ.discoveredBadgesCount ?? 0

  const stats: StatItem[] = [
    { icon: <h5 className="rounds-r">R</h5>, value: `${displayedRound}/${champ.rounds?.length || 0}` },
    { icon: <PersonIcon />, value: `${champ.competitors?.length || 0}/${champ.settings?.maxCompetitors || 0}` },
  ]

  if (showDrivers) {
    stats.push({ icon: <SportsMotorsportsIcon />, value: champ.series?.drivers?.length || 0 })
  }

  stats.push({ icon: <WorkspacePremiumIcon />, value: `${discoveredBadges}/${totalBadges}` })

  return stats
}

// Builds stats array for ChampBannerStats using pre-calculated snapshot data.
export const buildChampBannerStatsFromSnapshot = (champ: userChampSnapshotType): StatItem[] => {
  return [
    { icon: <h5 className="rounds-r">R</h5>, value: `${champ.roundsCompleted}/${champ.totalRounds}` },
    { icon: <PersonIcon />, value: `${champ.competitorCount}/${champ.maxCompetitors}` },
    { icon: <WorkspacePremiumIcon />, value: `${champ.discoveredBadges}/${champ.totalBadges}` },
  ]
}

// ============================================
// Competitor Data Functions
// ============================================

// Return type for getCompetitors function.
export type CompetitorWithStatus = CompetitorEntryType & {
  isInactive: boolean
  isBanned: boolean
  isKicked: boolean
  isDeleted: boolean
}

// Helper to get competitor ID from entry (uses snapshot for deleted users).
const getCompetitorId = (entry: CompetitorEntryType): string | undefined => {
  return entry.competitor?._id ?? entry.deletedUserSnapshot?._id
}

// Single source of truth for all competitors to render in both normal and adjudicator view.
// Combines round competitors with new joiners from champ.competitors and adds status flags.
export const getCompetitors = (
  viewedRound: RoundType | undefined,
  champCompetitors: fullUserType[],
  banned: fullUserType[] = [],
  kicked: fullUserType[] = [],
): CompetitorWithStatus[] => {
  if (!viewedRound) return []

  // Get competitors from current round data.
  const roundCompetitors = getCompetitorsFromRound(viewedRound)
  const roundCompetitorIds = new Set(roundCompetitors.map(c => getCompetitorId(c)).filter(Boolean))

  // Add competitors from champ.competitors who aren't in round data (new joiners).
  const newJoiners = champCompetitors
    .filter(c => !roundCompetitorIds.has(c._id))
    .map(c => ({
      competitor: c,
      bet: null,
      points: 0,
      totalPoints: 0,
      grandTotalPoints: 0,
      position: roundCompetitors.length + 1,
      updated_at: null,
      created_at: null,
      deleted: false,
    }))

  // Combine and add status flags.
  return [...roundCompetitors, ...newJoiners].map(c => {
    const competitorId = getCompetitorId(c)
    return {
      ...c,
      isInactive: competitorId ? isCompetitorInactive(competitorId, champCompetitors, banned, kicked) : true,
      isBanned: competitorId ? banned.some(b => b._id === competitorId) : false,
      isKicked: competitorId ? kicked.some(k => k._id === competitorId) : false,
      isDeleted: c.deleted ?? false,
    }
  })
}
